import heliotrope, {
  CommonIntegrations,
  ENVIRONMENT,
  HeliotropeReturn,
  InitOptions,
  SegmentUserObject,
  TrackOptions,
  UpdateStateOptions,
} from 'heliotrope';

// types
import { NonNullAssignment } from 'js/admin/controlpanel/types/abTest';
import { RegionName } from 'js/project/nds/types/region';
import { BackingCloudProvider, CloudProvider, InstanceSize } from 'js/project/nds/types/provider';
import { BasicClusterEventProperties } from 'js/project/nds/clusters/util/clusterTrackingUtil';

// constants
import {
  CLT_SIDEBAR_TRACK_CONTEXT,
  DbToolDrawer,
} from 'js/project/common/components/CommandLineTools/CommandLineToolsSidebar';
import { ValidCommand } from 'js/project/nds/clusters/components/CommandLineToolsPage/utils';
import { UserGoals } from 'js/project/nds/clusters/components/UserGoalResources';

// utils
import { CloudTeams, sendError } from 'js/common/utils/bugsnag';
import { getAbTestPersistedProps } from 'js/project/common/util/abTestUtil';
import { handleAdBlocker } from 'js/common/utils/adBlocker';
import getHeliotropeEnvironment from 'js/common/utils/getHeliotropeEnvironment';
import { TestGroupName, TestName } from 'js/project/common/constants/index';
import { TriggerNames as FlexConsultingTriggerNames } from 'js/project/common/util/flexConsultingUpsellExperimentUtil';
import { LastDataExplorerVisitedEventProps, MonitoringIdType } from 'js/project/metrics/util/lastDataExplorerVisited';

export type SegmentEvents = typeof SEGMENT_EVENTS[keyof typeof SEGMENT_EVENTS];

type TrackArgs = [SegmentEvents, TrackProperties, OptionsProperties?, ((...args: Array<any>) => void)?];
type PageArgs = [string, PageProperties?];
type IdentifyArgs = [object, number?];

interface InitProperties {
  analyticsEnabled: boolean;
  segmentId: string;
  appId: string;
  userId?: string;
  orgId?: string;
  groupId?: string;
  testAssignments?: Array<NonNullAssignment>;
  appEnv: AppEnv;
  accountCentralUrl: string;
  shouldRequestAuid?: boolean;
}

export enum AppEnv {
  LOCAL = 'LOCAL',
  QA = 'QA',
  DEV = 'DEV',
  PROD = 'PROD',
  TEST = 'TEST',
  STAGING = 'STAGING',
  HOSTED = 'HOSTED',
}

export interface OptionsProperties {
  timeout?: number;
}

interface PersistedProperties {
  cloud_user_id?: string;
  org_id?: string;
  group_id?: string;
  experiment_ids?: Array<string>;
  experiment_variant_ids?: Array<string>;
  test_assignments?: Array<string>;
}

interface AnalyticsProperties {
  track: (...args: TrackArgs) => Promise<HeliotropeReturn>;
  page: (...args: PageArgs) => Promise<HeliotropeReturn>;
  identify: (...args: IdentifyArgs) => Promise<HeliotropeReturn>;
  user: () => SegmentUserObject | undefined;
  updateState: (newState: UpdateStateOptions) => Promise<HeliotropeReturn>;
  reset: () => void;
}

const getIntegrations = (orgId?: string): CommonIntegrations => {
  let integrations;

  if (orgId) {
    integrations = buildIntegrationsObject(orgId);
  } else {
    integrations = {};
  }

  integrations['Google Analytics'] = true;

  return integrations;
};

const initializeAnalytics = async ({
  analyticsEnabled,
  segmentId,
  appId,
  userId,
  orgId,
  groupId,
  testAssignments,
  appEnv,
  accountCentralUrl,
  shouldRequestAuid = true,
}: InitProperties): Promise<void> => {
  if (!analyticsEnabled) {
    return;
  }
  const environment: ENVIRONMENT = getHeliotropeEnvironment(accountCentralUrl, appEnv);
  const initOptions: InitOptions = {
    enabled: analyticsEnabled,
    segmentId,
    appId,
    integrations: getIntegrations(orgId),
    canSetAuidCookie: true,
    environment,
    shouldRequestAuid,
  };
  const persistedProperties: PersistedProperties = testAssignments ? getAbTestPersistedProps(testAssignments) : {};

  if (userId) {
    persistedProperties.cloud_user_id = userId;
  }

  if (orgId) {
    persistedProperties.org_id = orgId;
  }

  if (groupId) {
    persistedProperties.group_id = groupId;
  }

  if (Object.keys(persistedProperties).length > 0) {
    initOptions.persistedProperties = persistedProperties;
  }
  try {
    await heliotrope.init(initOptions);
  } catch (error) {
    sendError({ error, team: CloudTeams.AtlasGrowth });
  }

  handleAdBlocker();
};

const analytics: AnalyticsProperties = {
  track: async (...args) => {
    const [eventName, properties, options = {}] = args;
    const trackOpts: TrackOptions<TrackProperties> = { eventName, properties };

    // Add timeout if passed in:
    const { timeout } = options;
    if (timeout) {
      trackOpts.timeout = timeout;
    }

    try {
      // await needed for handling promise rejections as well as thrown errors
      return await heliotrope.track(trackOpts);
    } catch (error) {
      sendError({ error, team: CloudTeams.AtlasGrowth });
      return error;
    }
  },
  page: async (...args) => {
    const [pageName, properties] = args;

    try {
      return await heliotrope.page({ pageName, properties });
    } catch (error) {
      sendError({ error, team: CloudTeams.AtlasGrowth });
      return error;
    }
  },
  identify: async (...args) => {
    const [traits, timeout] = args;

    try {
      return await heliotrope.identify({ traits, timeout });
    } catch (error) {
      sendError({ error, team: CloudTeams.AtlasGrowth });
      return error;
    }
  },
  user: () => heliotrope.user(),
  updateState: async (newState) => {
    try {
      return await heliotrope.updateState(newState);
    } catch (error) {
      sendError({ error, team: CloudTeams.AtlasGrowth });
      return error;
    }
  },
  reset: () => heliotrope.reset(),
};

// cluster description metadata from clusterTrackingUtil.js
export interface ClusterDescriptionTrackProperties {
  cloud_provider?: string;
  region?: string;
  cross_region?: boolean;
  global_cluster?: boolean;
  instance_size?: string;
  disk_size_gb?: number;
  IOPS_speed?: number;
  auto_expand_storage?: boolean;
  db_version?: string;
  backup_selected?: boolean;
  backup_option?: string;
  sharding?: boolean;
  shard_count?: number;
  BI_connector?: boolean;
  BI_node_type?: string;
  encryption_key_management?: boolean;
  key_index_limit?: boolean;
  require_indexes?: boolean;
  server_side_JS?: boolean;
  cluster_id?: string;
  cluster_name?: string;
  cluster_unique_id?: string;
  data_size?: any;
  auto_scaling_compute_enabled?: boolean;
  auto_scaling_compute_scaledown_enabled?: boolean;
  auto_scaling_compute_max_instance?: string;
  auto_scaling_compute_min_instance?: string;
}

/**
 * @deprecated
 *
 * Please do not extend this interface with further properties!
 *
 * Having one large tracking property interface with many optional properties leads to
 * loose type definitions and potentionally incorrect event tracking. Over time, this interface will be
 * whittled down and removed.
 *
 * Instead, please define a new interface specific to the track event you're introducing with as few optional properties
 * as possible. (Similar to RegionSelectedEvent below.) Comment each property as is sensible and ensure each property
 * name is separated by underscores (_) instead of being camelCased.
 *
 * Once a new interface is created, add it to the TrackProperties type union to use it in a `track` call downstream.
 * */
export interface TrackPropertiesLegacy extends ClusterDescriptionTrackProperties {
  context?: string;
  action?: string;
  value?: string;
  environment?: string;
  signup_source?: string;
  signup_method?: string;
  error_code?: string;
  error_message?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  country?: string;
  isEdit?: boolean;
  pre_selected_tier?: InstanceSize;
  userAgent?: string;
  revenue?: number;
  quantity?: number;
  price?: number;
  sku?: string;
  name?: string;
  referrer?: string;
  has_project_name_changed?: boolean;
  has_company_name_changed?: boolean;
  preferred_programming_language?: string | undefined;
  optimizelyEndUserId?: string;
  url?: string;
  tck?: string;
  ad_id?: string;
  ad_id_set?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_source?: string;
  utm_term?: string;
  utm_medium?: string;
  utm_adid?: string;
  utm_ad?: string;
  utm_ad_campaign_id?: string;
  gclid?: string;
  form_url?: string;
  hourly_cost?: string;
  tier?: string;
  error_count?: number;
  step_name?: string;
  nextUrl?: string;
  pathfinder_filter?: string;
  goal?: string | undefined;
  app_use_case?: string;
  app_use_case_other?: string;
  user_type?: string;
  is_temporary?: boolean;
  has_payment_method?: boolean;
  tooltip_id?: string;
  campaign?: string;
  search_string?: string;
}

interface PathSelectorCtaClickedEvent {
  filterVariant: string;
}

interface TriggersPreimageStateChangeEvent {
  new_preimage_state: boolean;
}

interface DataAPIEnableDisableEvent {
  context: string;
  action: string;
  tier: InstanceSize;
  cloudProvider: CloudProvider | BackingCloudProvider;
  region: RegionName;
}

interface DataAPISnippetCopiedEvent {
  context: string;
  action: string;
  snippetLanguage: string;
}

export interface ExperimentViewedBaseEvent {
  test_name: TestName;
  // Note we are sending in TestGroupName under two params. test_group_id is being kept around for backwards compat,
  // and will be removed when other areas of the system have been updated.
  test_group_id: TestGroupName;
  test_group_name: TestGroupName;
  entity_type: NonNullAssignment['entityType'];
  entity_id: NonNullAssignment['entityId'];
  test_tag: NonNullAssignment['tag'];
  assignment_date: NonNullAssignment['assignmentDate'];
  test_group_database_id: NonNullAssignment['testGroupDatabaseId'];
  test_id: NonNullAssignment['testId'];
  test_assignment: string;
}

interface ExperimentViewedFlexUpsellExperimentProperties {
  trigger: FlexConsultingTriggerNames;
}

interface ExperimentViewedM0ClusterCardCTAsExperimentProperties {
  hasConnections?: boolean;
  hasData?: boolean;
}

interface LocalCurrencyExperimentProperties {
  currency_selected?: string;
  selected_currency?: string;
}

export type ExperimentViewedAdditionalProperties =
  | ExperimentViewedFlexUpsellExperimentProperties
  | ExperimentViewedM0ClusterCardCTAsExperimentProperties;

export type ExperimentEventViewed =
  | ExperimentViewedBaseEvent
  | (ExperimentViewedBaseEvent & ExperimentViewedAdditionalProperties);

// event fired when a region is selected in the cluster builder region picker
interface RegionSelectedEvent {
  // which cluster builder form the event was fired from
  deployment_type: string;
  // e.g. US_EAST_1
  region_key: string;
  // a human legible location for a region, e.g. N. Virginia
  region_location: string;
  // whether or not a free tier is available for the newly selected region
  free_tier_available: boolean;
  // if the selected region is a grayed out paid tier region (experiment AG-360)
  grayed_out_paid_tier: boolean;
}

interface ClusterCardTipClick {
  context: string;
  action: string;
  tip_position?: string;
  instance_size?: string;
  tip_variant?: string;
}
interface LastVisitedDataExplorerUpdatedEvent {
  context: LastDataExplorerVisitedEventProps.CONTEXT;
  action: LastDataExplorerVisitedEventProps.ACTION;
  monitoring_id: string;
  monitoring_id_type: MonitoringIdType;
}

export interface PurchaseButtonClickedEvent {
  action: string;
  context: string;
  ctaRef?: string;
}

export interface PartnerLinkOrgButtonClickedEvent {
  action: string;
  context: string;
  org_id: string;
}

export interface FeatureUpdatesTriggerClickedEvent {
  action: string;
  context: string;
  copy: string;
  unread_count: string;
}

export interface TieredClusterSubmittedSuccessfullyEvent extends BasicClusterEventProperties {
  isEdit: boolean;
  pre_selected_tier?: InstanceSize;
  context: string;
  hourly_cost: string;
  region_autoselect: RegionName;
  user_country_code: string;
}

export interface ServerlessClusterSubmittedSuccessfullyEvent extends BasicClusterEventProperties {
  context: string;
  region_autoselect: RegionName;
  user_country_code: string;
}

type ClusterSubmittedSuccessfullyEvent =
  | ServerlessClusterSubmittedSuccessfullyEvent
  | TieredClusterSubmittedSuccessfullyEvent;

export enum FormType {
  STANDARD = 'standard',
  REGISTRATION = 'registration',
}

export enum EmailType {
  STANDARD = 'standard',
  BUSINESS = 'business',
}

export interface RegistrationFormSubmittedEvent {
  context: string;
  action: string;
  email_type: EmailType;
}

export interface IpWhitelistFormSubmittedEvent {
  context: string;
  adding_current_ip_address: boolean;
}

interface CLTDropdownBase {
  context: typeof CLT_SIDEBAR_TRACK_CONTEXT;
}

export interface CLTSidebarDrawerEvent extends CLTDropdownBase {
  action: 'Drawer Opened';
  drawer_name: DbToolDrawer;
}

export interface CLTSidebarCodeSampleEvent extends CLTDropdownBase {
  action: 'Code Sample Copied';
  code_sample_name: ValidCommand;
}

export interface CLTSidebarDropdownEvent extends CLTDropdownBase {
  action: 'Cluster Dropdown Changed';
}

export interface SurfaceUserGoalsLinkClickedEvent {
  action: string;
  context: 'User Goal Resources';
  section: UserGoals;
}

export interface PayWithAWSButtonClickedEvent {
  action: 'Pay with AWS Button Clicked';
}

export interface SettingsToggleCheckedEvent {
  context: string;
  settings_name: string;
  checked: boolean;
}

export interface PagerDutyActivationEvent {
  configuration_method: string;
}

// A union of all possible tracking property shapes
export type TrackProperties =
  | TrackPropertiesLegacy
  | ExperimentEventViewed
  | PathSelectorCtaClickedEvent
  | TriggersPreimageStateChangeEvent
  | RegionSelectedEvent
  | ClusterCardTipClick
  | LastVisitedDataExplorerUpdatedEvent
  | PurchaseButtonClickedEvent
  | ClusterSubmittedSuccessfullyEvent
  | RegistrationFormSubmittedEvent
  | DataAPIEnableDisableEvent
  | DataAPISnippetCopiedEvent
  | PartnerLinkOrgButtonClickedEvent
  | FeatureUpdatesTriggerClickedEvent
  | IpWhitelistFormSubmittedEvent
  | CLTSidebarDrawerEvent
  | CLTSidebarCodeSampleEvent
  | CLTSidebarDropdownEvent
  | LocalCurrencyExperimentProperties
  | SurfaceUserGoalsLinkClickedEvent
  | PayWithAWSButtonClickedEvent
  | SettingsToggleCheckedEvent
  | PagerDutyActivationEvent;

interface RegistrationPage {
  signup_source: string;
  isFromMp: boolean;
  marketplaceSource: string;
}

interface LoginPage {
  isFromMp: boolean;
  marketplaceSource: string;
}

interface GenericPageProperties {
  title?: string;
  url?: string;
  path?: string;
  search?: string;
  referrer?: string;
  signup_source?: string;
  userId?: string;
  resend?: boolean;
}

// A union of all possible page property shapes
export type PageProperties = RegistrationPage | LoginPage | GenericPageProperties;

// The below are the only supported event names for Segment events
// Analytics should be sent with these event names, with more granular information as event properties
const SEGMENT_EVENTS = {
  UX_ACTION_PERFORMED: 'UX Action Performed',
  BUTTON_CLICKED: 'Button Clicked',
  TOOLTIP_VIEWED: 'Tooltip Viewed',
  MIGRATE_CLICKED: 'Migrate Clicked',
  MIGRATE_MODAL_EXITED: 'Migrate Model Exited',
  MIGRATE_MODAL_CLICKED_READY: 'Migrate Modal Clicked Ready',
  CREDENTIALS_PROVIDED: 'Credentials Provided',
  CUTOVER_STARTED: 'Cutover Started',
  REGISTRATION_FORM_SUBMITTED: 'Registration Form Submitted',
  REGISTRATION_FORM_SUBMITTED_BUSINESS_EMAIL: 'Registration Form Submitted - Business Email',
  REGISTRATION_FAILED: 'Registration Failed',
  REGISTRATION_SUCCESS_SEEN: 'Registration Success Seen',
  VERCEL_CONFIRMATION_SEEN: 'Vercel Confirmation Page Seen',
  TOS_ACCEPTANCE_FAILED: 'ToS Acceptance Failed',
  FORM_SUBMITTED: 'Form Submitted',
  DATABASE_USER_FORM_SUBMITTED: 'Database User Form Submitted',
  IP_WHITELIST_FORM_SUBMITTED: 'IP Whitelist Form Submitted',
  IP_WHITELIST_ENTRY_REMOVED: 'IP Whitelist Entry Removed',
  EXPERIMENT_VIEWED: 'Experiment Viewed',
  PROJECT_SETTINGS_TOGGLE_CHECKED: 'Project Settings Toggle Checked',
  USER_SETTINGS_TOGGLE_CHECKED: 'User Settings Toggle Checked',

  // events from billing overview page
  APPLY_CREDIT_MODAL_SHOWN: 'Apply Credit Modal Shown',
  APPLY_CREDIT_CODE_APPLIED_SUCCESSFULLY: 'Credit Activation Code Applied Successfully',

  // events only fired from the cluster builder (general)
  CLUSTER_SUBMITTED_SUCCESSFULLY: 'Cluster Submitted Successfully',
  CLUSTER_PROVISIONED: 'Cluster Provisioned',
  /**
   * CLUSTER_TIER_BUMPED: when user selects region/provider where tier unavailable => auto selects tier
   * @repro Provider change: Dedicated (N. Virginia) -> choose M700 -> select GCP -> notice M10 now chosen
   * @repro Region change: Dedicated (N. Virginia) -> choose M700 -> select Sau Paulo -> notice M10 now chosen
   */
  CLUSTER_TIER_BUMPED: 'Cluster Tier Bumped',

  // events only fired from the cluster builder payment modal
  CHECKOUT_STARTED: 'Checkout Started',
  CHECKOUT_STEP_VIEWED: 'Checkout Step Viewed',
  CHECKOUT_STEP_COMPLETED: 'Checkout Step Completed',
  DEPRECATED_CODE_PATH: 'Deprecated Code Path',
  ORDER_COMPLETED: 'Order Completed',
  PAYMENT_MODAL_SHOWN: 'Payment Modal Shown',
  PAYMENT_INFO_SUBMITTED_SUCCESSFULLY: 'Payment Info Submitted Successfully',
  PAYMENT_INFO_ERROR: 'Payment Info Error',
  PAYMENT_MODAL_CLOSED: 'Payment Modal Closed',
  PAYMENT_FULL_PAGE_SHOWN: 'Payment Full-Page Shown',
  PAYMENT_FULL_PAGE_CLOSED: 'Payment Full-Page Closed',
  PAYMENT_INFO_ENTERED: 'Payment Info Entered',
  PAYMENT_INFO_EDIT_INITIATED: 'Payment Info Edit Initiated',

  LOAD_SAMPLE_DATA_CLICKED: 'Load Sample Data Clicked',
  LOAD_SAMPLE_DATA_CANCELLED: 'Load Sample Data Cancelled',
  LOAD_SAMPLE_DATA_STARTED: 'Load Sample Data Started',
  SUPPORT_PLAN_ACTIVATED: 'Support Plan Activated',
  SUPPORT_PLAN_CANCELLED: 'Support Plan Cancelled',

  // Data Explorer CRUD Operations
  DOCUMENT_INSERTED: 'Document Inserted',
  DOCUMENT_INSERTED_MANY: 'Many Documents Inserted via JSON',
  DOCUMENT_CLONED: 'Document Cloned',
  DOCUMENT_CLONED_MANY: 'Document Cloned with Multiple Insertions via JSON',
  DOCUMENT_UPDATED: 'Document Updated',
  DOCUMENT_DELETED: 'Document Deleted',
  QUERY_EXECUTED: 'Query Executed',
  NAMESPACE_CREATED: 'Namespace Created',
  INDEX_CREATED: 'Index Created',
  INDEX_DELETED: 'Index Deleted',
  NAMESPACE_DELETED: 'Namespace Deleted',

  // Peering
  PEERING_CONNECTION_SUBMITTED: 'Peering Connection Submitted',

  // MONGO NAV
  NAVIGATION_CLICKED: 'Navigation Clicked',
  TOP_NAV_SEARCH: 'Top Nav Search',

  // Online Archive
  VIEWED_ONLINE_ARCHIVE: 'Viewed Online Archive',

  // Data Lake
  DATA_LAKE_CREATED: 'Data Lake Created',

  // For all steppers:
  STEP_COMPLETED: 'Step Completed',

  // Cluster Card
  CLUSTER_CARD_VIEW_LOADED: 'Cluster Card View Loaded',

  // Cloud Feature Updates
  CLOUD_FEATURE_UPDATES_TRIGGER_CLICKED: 'Cloud Feature Updates Trigger Clicked',

  // Sample Data Load
  CLICKED_ADD_SAMPLE_DATA: 'Clicked Add Sample Data',

  // Local Currency
  CLICKED_CURRENCY_SELECTOR: 'Clicked Currency Selector',
  CLICKED_CURRENCY_TOGGLE: 'Clicked Currency Toggle',

  // Go To
  GO_TO_SHOWN: 'Atlas Goto Shown',
} as const;

const PAGE_CALLS = {
  REGISTRATION_SUCCESS: 'Registration Success',
  SIGN_IN: 'Sign In',
  REGISTRATION: 'Registration',
  TERMS_OF_SERVICE: 'Terms of Service',
  ATLAS_SEARCH: 'Atlas Search',
  FLEX_CONSULTING: 'Flex Consulting',
  OPEN_PROJECT_ALERTS: 'Open Project Alerts',
  VERIFICATION_EMAIL: 'Verification Email',
  VERIFICATION_EMAIL_BANNER: 'Verification Email Banner',
  MANAGE_PROJECT_ALERTS: 'Manage Project Alerts',
  OPEN_ORG_ALERTS: 'Open Org Alerts',
  MANAGE_ORG_ALERTS: 'Manage Org Alerts',
  ALL_CLUSTERS: 'All Clusters',
  DATA_API: 'Data Api',
} as const;

const buildIntegrationsObject = (orgId: string) => ({
  Amplitude: {
    groups: {
      Organization: orgId,
    },
  },
});

const sanitizeQueryString = () => {
  const search = window.location.search.replace(/^\?/, '');

  let sanitized = '';

  search.split('&').forEach((keyeqval) => {
    const lrvalues = keyeqval.split('=');
    const key = decodeURI(lrvalues[0]);
    const rvalue = lrvalues[1];

    if (key === '_ga' || key === 'gclid' || key === 'adpos' || !!key.match(/^utm_/)) {
      sanitized += sanitized === '' ? '?' : '&';
      sanitized += encodeURI(key);
      sanitized += '=';
      sanitized += rvalue;
    }
  });

  return sanitized;
};

// Given an array of strings this function will return a concatenated string of all elements
// that are not empty nor ObjectID
// i.e. given an array of ['foo', 'bar', '55a06f300cc6908d8bb5632b'] it should return '/foo/bar'
const addPathElements = (arr, resultString) => {
  return arr.reduce((acc, pathElem) => {
    if (pathElem && !pathElem.match(/^[0-9A-Fa-f]+$/)) {
      // skip empties and hex-looking strings.
      acc += `/${pathElem}`;
    }
    return acc;
  }, resultString);
};

const dataExplorerPathRegex = 'explorer\\/((.+\\/){2})?(.+)?';

const sanitizeDataExplorerPath = (path) => {
  const pathMatch = path.match(dataExplorerPathRegex);
  // remove database and collection name
  if (pathMatch && pathMatch.index && pathMatch[1]) {
    let sanitizedPath = '';
    path.split(pathMatch[1]).map((subst) => (sanitizedPath += subst));
    return sanitizedPath;
  }
  // remove database name
  if (pathMatch && pathMatch[3]) {
    return path.slice(0, -pathMatch[3].length);
  }
  return path;
};

const sanitizeLocation = (loc = window.location) => {
  let sanitized = '';
  const pathDelimiterRegex = /^\|\/$/;
  sanitized = addPathElements(loc.pathname.replace(pathDelimiterRegex, '').split('/'), sanitized);

  if (loc.search) {
    sanitized += sanitizeQueryString();
  }

  if (loc.hash) {
    let hash = sanitizeDataExplorerPath(loc.hash);
    hash = hash.replace(pathDelimiterRegex, '').split('/');
    sanitized += hash[0];
    hash = hash.slice(1);
    sanitized = addPathElements(hash, sanitized);
  }

  return sanitized;
};

export default analytics;
export { initializeAnalytics, SEGMENT_EVENTS, PAGE_CALLS, buildIntegrationsObject, sanitizeLocation, ENVIRONMENT };

export type { HeliotropeReturn };
